import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Send, ArrowRight, ChevronDown, MessageCircle, Check, Calendar, Clock, User, Mail, Mic, Square } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { trackChatOpened, trackChatMessage, trackLeadCaptured, trackCTAClick } from "../utils/analytics";
import { useBasket } from "../context/BasketContext";
import { ADVISORS, DEFAULT_ADVISOR, getAdvisor } from "../data/advisors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map the 10 STRATIQ advisors (Doc 20 §7) into the chat shape the rest of the
// component already uses. The chat opens with Sarah by default; advisors swap
// mid conversation when the user's intent fits another specialism.
const OPERATORS = ADVISORS.map(a => ({ id: `op_${a.id}`, advisor_id: a.id, name: a.name, role: a.role, img: a.img }));

const STAGGER_MS = [5 * 60 * 1000, 7 * 60 * 1000]; // 5min, 7min alternating
const LS_PREFIX = "stratiq_assistant_";

function cleanResponse(text) {
  // System prompt already forbids markdown / em dashes / hyphens for stylistic
  // effect, so no client side rewriting is needed. Just trim whitespace.
  return (text || "").trim();
}

function getDefaultOperator() {
  // Always start with Sarah K. (Senior Strategy Advisor) per Doc 20 §7.
  return OPERATORS.find(o => o.advisor_id === DEFAULT_ADVISOR.id) || OPERATORS[0];
}

function OperatorAvatar({ src, alt, size = "w-7 h-7", className = "" }) {
  return (
    <div className={`${size} rounded-full overflow-hidden flex-shrink-0 ${className}`}>
      <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" />
    </div>
  );
}

function ChatToggleButton({ onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.button
      data-testid="assistant-toggle-btn"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      whileTap={{ scale: 0.97 }}
      className="relative bg-slate-900 text-white rounded-2xl w-[200px] h-[56px] flex items-center justify-center gap-3 shadow-[0_4px_20px_rgba(0,0,0,0.3),0_0_30px_rgba(59,130,246,0.15)] hover:shadow-[0_8px_32px_rgba(59,130,246,0.35),0_0_40px_rgba(59,130,246,0.2)] transition-all duration-300 overflow-hidden border border-blue-500/20"
    >
      {/* Glow effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-transparent to-blue-600/10 opacity-0 hover:opacity-100 transition-opacity duration-300" />
      <div className="absolute inset-0 rounded-2xl" style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1)' }} />
      
      <AnimatePresence mode="wait">
        {!hovered ? (
          <motion.div key="bubble" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }} className="flex items-center gap-3">
            <MessageCircle size={20} />
            <span className="text-sm font-semibold tracking-wide">Chat with us</span>
          </motion.div>
        ) : (
          <motion.div key="bot" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }} className="flex items-center gap-3">
            <svg width="22" height="18" viewBox="0 0 18 14" fill="none">
              <rect x="1" y="3" width="16" height="11" rx="4" fill="white" fillOpacity="0.9" />
              <rect x="0" y="0" width="4" height="3" rx="1.5" fill="white" fillOpacity="0.5" transform="translate(7, 0)" />
              <circle cx="6.5" cy="8.5" r="1.5" fill="#3B82F6" />
              <circle cx="11.5" cy="8.5" r="1.5" fill="#3B82F6" />
            </svg>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">We're online</span>
              <div className="flex gap-[3px]">
                {[0, 1, 2].map(i => (
                  <motion.span key={i} className="block w-[4px] h-[4px] rounded-full bg-blue-400" animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }} transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15, ease: "easeInOut" }} />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="absolute top-2 right-2 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-slate-900 shadow-[0_0_8px_rgba(74,222,128,0.6)]" />
    </motion.button>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

export default function AIAssistant() {
  const { isOpen: basketIsOpen } = useBasket();
  const [stage, setStage] = useState("closed");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [operator, setOperator] = useState(getDefaultOperator);
  const [sessionId] = useState(() => Math.random().toString(36).slice(2));
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [formError, setFormError] = useState("");
  const [userInteracting, setUserInteracting] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const staggerTimerRef = useRef(null);
  const isMobile = useIsMobile();

  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  // Tier recommender state
  const [recommending, setRecommending] = useState(false);
  const [lockingTier, setLockingTier] = useState(false);

  const userMessageCount = messages.filter(m => m.role === "user").length;

  const requestTierRecommendation = useCallback(async () => {
    if (recommending) return;
    setRecommending(true);
    if (navigator.vibrate) navigator.vibrate(15);
    try {
      const last = [...messages].reverse().find(m => m.role === "user")?.content || "";
      const res = await axios.post(`${API}/assistant/recommend-tier`, { session_id: sessionId, last_user_message: last });
      const tier = res.data || {};
      setMessages(prev => [...prev, { role: "tier", tier }]);
      try {
        if (window.gtag) window.gtag("event", "tier_recommended", { tier_key: tier.tier_key, value: (tier.amount_pence || 0) / 100, currency: "GBP" });
      } catch (e) {}
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: "I couldn't pull a recommendation just now. Try asking me which tier fits your business." }]);
    } finally {
      setRecommending(false);
    }
  }, [messages, sessionId, recommending]);

  const lockTier = useCallback(async (tier) => {
    if (lockingTier) return;
    setLockingTier(true);
    try {
      const name = formData.name || "Visitor";
      const email = formData.email;
      if (!email) {
        setMessages(prev => [...prev, { role: "assistant", content: "I need your email to lock this in. Please share it in chat and I will set up checkout." }]);
        setLockingTier(false);
        return;
      }
      // GA4: add_to_cart
      try {
        if (window.gtag) window.gtag("event", "add_to_cart", { currency: "GBP", value: (tier.amount_pence || 0) / 100, items: [{ item_id: tier.tier_key, item_name: tier.name, price: (tier.amount_pence || 0) / 100, quantity: 1 }] });
      } catch (e) {}
      const res = await axios.post(`${API}/stripe/subscribe`, {
        tier_key: tier.tier_key,
        name,
        email,
        origin_url: window.location.origin,
        session_id: sessionId,
      });
      // GA4: begin_checkout
      try {
        if (window.gtag) window.gtag("event", "begin_checkout", { currency: "GBP", value: (tier.amount_pence || 0) / 100, items: [{ item_id: tier.tier_key, item_name: tier.name, price: (tier.amount_pence || 0) / 100, quantity: 1 }] });
      } catch (e) {}
      window.location.href = res.data.checkout_url;
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: "Couldn't reach Stripe just now. Please try again in a moment." }]);
    } finally {
      setLockingTier(false);
    }
  }, [formData, sessionId, lockingTier]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);
  useEffect(() => { if (stage === "chat") inputRef.current?.focus(); }, [stage]);

  // ─── STAGGERED RE-ENGAGEMENT LOGIC ───
  useEffect(() => {
    // On mobile, never auto open the showcase. only show the floating button
    if (isMobile) return;

    const hasAutoOpened = localStorage.getItem(`${LS_PREFIX}autoOpened`);

    // First visit: auto open once after 5s
    if (!hasAutoOpened) {
      const timer = setTimeout(() => {
        if (stage === "closed") {
          localStorage.setItem(`${LS_PREFIX}autoOpened`, "true");
          setStage("showcase");
        }
      }, 5000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // Schedule next auto open after close (desktop only)
  const scheduleNextOpen = useCallback(() => {
    if (isMobile) return; // No auto open on mobile
    if (staggerTimerRef.current) clearTimeout(staggerTimerRef.current);

    const idx = parseInt(localStorage.getItem(`${LS_PREFIX}staggerIdx`) || "0", 10);
    const delay = STAGGER_MS[idx % STAGGER_MS.length];

    localStorage.setItem(`${LS_PREFIX}lastClosed`, Date.now().toString());
    localStorage.setItem(`${LS_PREFIX}staggerIdx`, (idx + 1).toString());

    staggerTimerRef.current = setTimeout(() => {
      if (!userInteracting) {
        setStage("showcase");
      }
    }, delay);
  }, [userInteracting, isMobile]);

  // Cleanup stagger timer on unmount
  useEffect(() => {
    return () => {
      if (staggerTimerRef.current) clearTimeout(staggerTimerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") mediaRecorderRef.current.stop();
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  }, []);

  // Listen for global "open-assistant" events fired by Talk to a STRATIQ Advisor buttons
  useEffect(() => {
    const onOpen = () => {
      if (staggerTimerRef.current) clearTimeout(staggerTimerRef.current);
      setStage("showcase");
      try { trackChatOpened("global_event"); } catch (e) {}
    };
    window.addEventListener("open-assistant", onOpen);
    return () => window.removeEventListener("open-assistant", onOpen);
  }, []);

  const handleClose = useCallback(() => {
    // Fire session summary if there was meaningful conversation
    if (messages.length >= 2) {
      axios.post(`${API}/assistant/end-session`, { session_id: sessionId }).catch(() => {});
    }
    setStage("closed");
    scheduleNextOpen();
  }, [scheduleNextOpen, messages.length, sessionId]);

  const handleMinimize = useCallback(() => {
    setStage("closed");
    scheduleNextOpen();
  }, [scheduleNextOpen]);

  const handleManualOpen = useCallback(() => {
    // Manual open doesn't count as nag cycle. cancel any pending stagger
    if (staggerTimerRef.current) clearTimeout(staggerTimerRef.current);
    setStage("showcase");
    trackChatOpened("manual_toggle");
  }, []);

  const handleExplore = useCallback(() => {
    setUserInteracting(true);
    setStage("form");
    trackChatOpened("explore_click");
  }, []);

  const handleFormSubmit = useCallback(async () => {
    if (!formData.name.trim() || !formData.email.trim()) { setFormError("Please fill in your name and email."); return; }
    if (!/\S+@\S+\.\S+/.test(formData.email)) { setFormError("Please enter a valid email address."); return; }
    setFormError("");
    try {
      await axios.post(`${API}/assistant/lead`, { name: formData.name, email: formData.email, session_id: sessionId, operator_name: operator.name, operator_id: operator.id });
      // Track lead captured from chat
      trackLeadCaptured("ai_frontdesk", { email: formData.email, businessName: "" });
    } catch { /* Silent fail */ }

    // Canonical STRATIQ Front Desk opening (per System Prompt v2)
    const greeting = "Hi, I am the STRATIQ Front Desk Assistant. I can answer questions about our services, help you find the right tier for your business, or book a consultation with Aaron. What brings you here today?";

    setMessages([{ role: "assistant", content: greeting }]);
    setStage("chat");
  }, [formData, sessionId, operator]);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || loading) return;
    if (navigator.vibrate) navigator.vibrate(10);
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);
    trackChatMessage(true);
    const typingStart = Date.now();
    const MIN_TYPING_MS = 600;
    try {
      const res = await axios.post(`${API}/assistant/chat`, {
        message: userMsg,
        session_id: sessionId,
        operator_name: operator.name,
        advisor_id: operator.advisor_id || "sarah",
      });
      const elapsed = Date.now() - typingStart;
      if (elapsed < MIN_TYPING_MS) await new Promise(r => setTimeout(r, MIN_TYPING_MS - elapsed));
      const reply = cleanResponse(res.data.reply || res.data.response || "Sorry, I couldn't process that.");
      trackChatMessage(false);

      const additions = [];
      if (res.data.booking) {
        additions.push({ role: "assistant", content: reply });
        additions.push({ role: "booking", booking: res.data.booking });
      } else {
        additions.push({ role: "assistant", content: reply });
      }
      // Inline handoff card if backend suggests a different specialist
      if (res.data.suggest_handoff) {
        additions.push({ role: "handoff", handoff: res.data.suggest_handoff });
      }
      setMessages(prev => [...prev, ...additions]);
    } catch {
      const elapsed = Date.now() - typingStart;
      if (elapsed < MIN_TYPING_MS) await new Promise(r => setTimeout(r, MIN_TYPING_MS - elapsed));
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
    } finally { setLoading(false); }
  }, [input, loading, sessionId, operator.name, operator.advisor_id]);

  // Accept advisor handoff. Switches operator + drops a system style note.
  const acceptHandoff = useCallback((handoff) => {
    const next = OPERATORS.find(o => o.advisor_id === handoff.to_advisor_id) || OPERATORS[0];
    if (navigator.vibrate) navigator.vibrate(15);
    setMessages(prev => [
      ...prev,
      { role: "system", content: `Bear with me. Connecting you to ${handoff.to_advisor_name}.` },
    ]);
    setTimeout(() => {
      setOperator(next);
      const advisor = getAdvisor(handoff.to_advisor_id);
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: advisor.intro },
      ]);
    }, 900);
  }, []);

  const declineHandoff = useCallback(() => {
    setMessages(prev => prev.filter(m => m.role !== "handoff"));
  }, []);

  // Voice recording handlers
  const startRecording = useCallback(async () => {
    try {
      // Haptic feedback on start
      if (navigator.vibrate) navigator.vibrate(30);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1,
        }
      });

      // Pick best supported mime type (Safari doesn't support webm)
      let mimeType = "audio/webm;codecs=opus";
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "audio/webm";
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "audio/mp4";
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = ""; // let browser choose default
      }

      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
        setRecordingDuration(0);

        // Haptic on stop
        if (navigator.vibrate) navigator.vibrate([20, 50, 20]);

        const actualMime = mediaRecorder.mimeType || "audio/webm";
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMime });
        if (audioBlob.size < 100) return;

        setIsTranscribing(true);
        try {
          const fd = new FormData();
          // Use correct extension based on mime
          const ext = actualMime.includes("mp4") || actualMime.includes("m4a") ? ".m4a" : actualMime.includes("ogg") ? ".ogg" : ".webm";
          fd.append("file", audioBlob, `recording${ext}`);
          const res = await axios.post(`${API}/transcribe`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 30000,
          });
          if (res.data.text) {
            setInput(prev => (prev ? prev + " " : "") + res.data.text);
            inputRef.current?.focus();
            // Success haptic
            if (navigator.vibrate) navigator.vibrate(15);
          }
        } catch (err) { console.error("Transcription failed:", err); }
        finally { setIsTranscribing(false); }
      };

      mediaRecorder.start(250);
      setIsRecording(true);
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => setRecordingDuration(d => d + 1), 1000);
    } catch (err) {
      console.error("Microphone access denied:", err);
      alert("Please allow microphone access to use voice input.");
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") mediaRecorderRef.current.stop();
    setIsRecording(false);
  }, []);

  const toggleRecording = useCallback(() => {
    if (isRecording) stopRecording(); else startRecording();
  }, [isRecording, startRecording, stopRecording]);

  const formatDuration = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  // Render as portal to ensure fixed positioning works
  // Hide when basket drawer is open to prevent z-index overlap
  if (basketIsOpen) return null;

  return createPortal(
    <>
      {/* ─── STAGE 1: SHOWCASE WIDGET ─── */}
      <AnimatePresence>
        {stage === "showcase" && (
          <motion.div initial={{ opacity: 0, y: 30, scale: 0.92 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.95 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-20 right-3 w-[280px] sm:bottom-6 sm:right-4 sm:w-[320px] md:right-6 md:w-[340px]" style={{ zIndex: 2147483646 }} data-testid="assistant-showcase">
            <div className="bg-white rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.15)] border border-slate-200/80 overflow-hidden">
              <button onClick={handleMinimize} className="absolute top-3 right-3 z-10 p-1 rounded-full hover:bg-slate-100 transition-colors" data-testid="showcase-minimize">
                <ChevronDown size={16} className="text-slate-400" />
              </button>
              <div className="relative h-[140px] overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
                <img src="/smartsite_showcase.webp" alt="SmartSite™ Preview" className="w-full h-full object-cover opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent" />
              </div>
              <div className="px-5 pt-4 pb-2">
                <h3 className="text-lg font-bold text-slate-900 leading-snug">Want to know which tier fits?</h3>
                <p className="text-[13px] text-slate-500 mt-1.5 leading-relaxed">Talk to one of our 10 STRATIQ advisors. Tell us about your business, we will tell you the tier that fits and how the 90 day journey looks.</p>
              </div>
              <div className="px-5 pt-2 pb-4 space-y-2">
                <button onClick={handleExplore} className="w-full bg-slate-900 text-white text-sm font-semibold py-3 rounded-xl hover:bg-blue-600 transition-colors flex items-center justify-center gap-2" data-testid="showcase-explore-btn">
                  Talk to a STRATIQ Advisor <ArrowRight size={14} />
                </button>
                <button onClick={() => { window.location.href = "/pricing"; }} className="w-full bg-slate-100 text-slate-700 text-sm font-semibold py-3 rounded-xl hover:bg-slate-200 transition-colors" data-testid="showcase-pricing-btn">
                  View Subscription Plans
                </button>
              </div>
              <div className="px-5 pb-4">
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 cursor-pointer hover:border-slate-300 transition-colors" onClick={handleExplore}>
                  <span className="text-sm text-slate-400 flex-1">Write a message...</span>
                  <ArrowRight size={14} className="text-slate-400" />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── STAGE 2: EMAIL CAPTURE FORM ─── */}
      <AnimatePresence>
        {stage === "form" && (
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.95 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-20 right-3 w-[280px] sm:bottom-6 sm:right-4 sm:w-[320px] md:right-6 md:w-[340px]" style={{ zIndex: 2147483647 }} data-testid="assistant-form">
            <div className="bg-white rounded-2xl shadow-[0_16px_48px_rgba(0,0,0,0.18)] border border-slate-200/80 overflow-hidden">
              <div className="bg-slate-950 px-5 py-4 flex items-center justify-between">
                <button onClick={() => setStage("showcase")} className="text-slate-400 hover:text-white transition-colors text-xs font-medium" data-testid="form-back-btn">Back</button>
                <button onClick={handleClose} className="p-1 rounded-full hover:bg-white/10 transition-colors"><ChevronDown size={16} className="text-slate-400" /></button>
              </div>
              <div className="px-5 pt-5 pb-3 text-center">
                <OperatorAvatar src={operator.img} alt={operator.name} size="w-14 h-14" className="mx-auto mb-3 ring-2 ring-blue-500/30 ring-offset-2" />
                <p className="text-sm font-bold text-slate-900">{operator.name}</p>
                <p className="text-[11px] text-slate-500 font-medium">{operator.role}</p>
              </div>
              <div className="px-5 pb-3">
                <p className="text-sm text-blue-600 font-medium text-center mb-4">Just a few details to get started.</p>
                <div className="bg-slate-50 rounded-xl p-4 mb-4 border border-slate-100">
                  <div className="flex items-start gap-2.5">
                    <OperatorAvatar src={operator.img} alt={operator.name} size="w-6 h-6" className="mt-0.5" />
                    <p className="text-[13px] text-slate-600 leading-relaxed">Welcome to STRATIQ! Just a few details to prepare your personal consultation.</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1 block">Name <span className="text-red-400">*</span></label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                      className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all placeholder:text-slate-400"
                      placeholder="Your name" data-testid="form-name-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1 block">Email <span className="text-red-400">*</span></label>
                    <input type="email" value={formData.email} onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                      className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all placeholder:text-slate-400"
                      placeholder="your@email.com" data-testid="form-email-input" />
                  </div>
                </div>
                {formError && <p className="text-xs text-red-500 mt-2">{formError}</p>}
              </div>
              <div className="px-5 pb-5 pt-1">
                <button onClick={handleFormSubmit} className="w-full bg-slate-900 text-white text-sm font-semibold py-3 rounded-xl hover:bg-blue-600 transition-colors" data-testid="form-submit-btn">Start the Chat</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── STAGE 3: FULL CHAT ─── */}
      <AnimatePresence>
        {stage === "chat" && (
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.95 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid="assistant-chat-window"
            className="fixed bottom-0 right-0 w-full sm:bottom-6 sm:right-4 sm:w-[340px] md:right-6 md:w-[380px] sm:max-w-[calc(100vw-32px)] sm:rounded-2xl rounded-t-2xl bg-white shadow-[0_20px_48px_rgba(0,0,0,0.18)] border border-slate-200/80 flex flex-col overflow-hidden"
            style={{ zIndex: 2147483647, maxHeight: "min(540px, calc(100vh - 60px))" }}>
            {/* Header */}
            <div className="bg-slate-950 px-4 py-3 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <OperatorAvatar src={operator.img} alt={operator.name} size="w-9 h-9" className="ring-2 ring-blue-500/50" />
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-slate-950" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{operator.name}</p>
                  <p className="text-[10px] text-slate-400">{operator.role}</p>
                </div>
              </div>
              <button data-testid="assistant-close-btn" onClick={handleClose} className="p-1.5 rounded-full hover:bg-white/10 transition-colors">
                <X size={16} className="text-slate-400" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50 min-h-0">
              {messages.map((msg, i) => (
                msg.role === "handoff" ? (
                  <div key={i} className="flex justify-start gap-2" data-testid={`handoff-card-${msg.handoff?.to_advisor_id}`}>
                    <div className="max-w-[88%] rounded-2xl rounded-bl-md overflow-hidden shadow-md border border-blue-200 bg-white">
                      <div className="bg-gradient-to-br from-slate-900 to-slate-700 px-4 py-3 text-white flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20 flex-shrink-0">
                          <img src={msg.handoff?.to_advisor_img} alt={msg.handoff?.to_advisor_name} className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-white/65">Specialist available</p>
                          <h4 className="text-base font-bold leading-none">{msg.handoff?.to_advisor_name}</h4>
                          <p className="text-xs text-white/75 mt-0.5">{msg.handoff?.to_advisor_role}</p>
                        </div>
                      </div>
                      <div className="p-4 text-xs text-slate-700 leading-relaxed">
                        <p className="mb-3">{`This is more ${msg.handoff?.to_advisor_role}'s area. Want me to bring them in for you?`}</p>
                        <div className="flex gap-2">
                          <button data-testid={`handoff-accept-${msg.handoff?.to_advisor_id}`} onClick={() => acceptHandoff(msg.handoff)} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-blue-600 text-white text-xs font-semibold py-2 rounded-xl transition-colors">Yes please <ArrowRight size={12} /></button>
                          <button data-testid="handoff-decline" onClick={declineHandoff} className="px-3 inline-flex items-center justify-center text-xs font-semibold text-slate-500 hover:text-slate-900 rounded-xl transition-colors">Stay here</button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : msg.role === "system" ? (
                  <div key={i} className="flex justify-center" data-testid="handoff-system-note">
                    <p className="text-xs text-slate-400 italic px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100">{msg.content}</p>
                  </div>
                ) : msg.role === "tier" ? (
                  <div key={i} className="flex justify-start gap-2" data-testid={`tier-rec-card-${msg.tier?.tier_key || "unknown"}`}>
                    <OperatorAvatar src={operator.img} alt={operator.name} size="w-7 h-7" className="mt-1" />
                    <div className="max-w-[88%] rounded-2xl rounded-bl-md overflow-hidden shadow-md border border-blue-200 bg-white">
                      <div className="bg-gradient-to-br from-blue-500 to-indigo-700 px-4 py-3 text-white">
                        <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-white/75 mb-1">Recommended Tier</p>
                        <h4 className="text-lg font-extrabold leading-none">{msg.tier?.name || "STRATIQ Tier"}</h4>
                        <p className="text-base font-semibold mt-1">{msg.tier?.display_price}</p>
                      </div>
                      <div className="p-4 text-xs text-slate-800 leading-relaxed space-y-2">
                        {msg.tier?.summary && <p><span className="font-bold text-slate-900">What you get. </span>{msg.tier.summary}</p>}
                        {msg.tier?.reason && <p><span className="font-bold text-slate-900">Why this tier. </span>{msg.tier.reason}</p>}
                      </div>
                      <div className="px-4 pb-4">
                        <button
                          data-testid={`lock-tier-btn-${msg.tier?.tier_key}`}
                          onClick={() => lockTier(msg.tier)}
                          disabled={lockingTier}
                          className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-blue-600 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors disabled:opacity-60"
                        >
                          {lockingTier ? "Opening Stripe..." : "Lock This Tier"} <ArrowRight size={14} />
                        </button>
                        <p className="text-[10px] text-slate-400 text-center mt-2">Secure Stripe checkout. Cancel any time after 30 days.</p>
                      </div>
                    </div>
                  </div>
                ) : msg.role === "booking" ? (                  <div key={i} className="flex justify-start gap-2" data-testid="booking-confirmation-card">
                    <OperatorAvatar src={operator.img} alt={operator.name} size="w-7 h-7" className="mt-1" />
                    <div className="max-w-[85%] rounded-2xl rounded-bl-md overflow-hidden shadow-sm border border-green-200 bg-white">
                      <div className="bg-green-50 px-4 py-2.5 flex items-center gap-2 border-b border-green-100">
                        <Check size={14} className="text-green-600" />
                        <span className="text-xs font-bold text-green-700">Booking Confirmed</span>
                        {msg.booking.calendar_synced && <span className="ml-auto text-[10px] text-green-500 font-medium">Calendar Synced</span>}
                      </div>
                      <div className="px-4 py-3 space-y-1.5">
                        <div className="flex items-center gap-2 text-xs text-slate-600"><User size={12} className="text-slate-400" /><span>{msg.booking.name}</span></div>
                        <div className="flex items-center gap-2 text-xs text-slate-600"><Calendar size={12} className="text-slate-400" /><span>{new Date(msg.booking.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span></div>
                        <div className="flex items-center gap-2 text-xs text-slate-600"><Clock size={12} className="text-slate-400" /><span>{(() => { const [h,m] = msg.booking.time.split(":"); const hr = parseInt(h); return `${hr % 12 || 12}:${m} ${hr >= 12 ? "PM" : "AM"}`; })()}</span></div>
                        <div className="flex items-center gap-2 text-xs text-slate-600"><Mail size={12} className="text-slate-400" /><span>{msg.booking.email}</span></div>
                      </div>
                      <div className="px-4 py-2 bg-slate-50 border-t border-slate-100">
                        <p className="text-[10px] text-slate-400">A calendar invite has been sent to your email.</p>
                      </div>
                    </div>
                  </div>
                ) : (
                <div key={i} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  {msg.role === "assistant" && <OperatorAvatar src={operator.img} alt={operator.name} size="w-7 h-7" className="mt-1" />}
                  <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.role === "user" ? "bg-blue-600 text-white rounded-br-md" : "bg-white text-slate-700 rounded-bl-md shadow-sm border border-slate-100"
                  }`}>
                    {msg.role === "assistant" ? (
                      <div className="assistant-markdown">
                        <ReactMarkdown
                          components={{
                            p: ({children}) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
                            strong: ({children}) => <strong className="font-bold text-slate-900">{children}</strong>,
                            em: ({children}) => <em className="italic">{children}</em>,
                            ul: ({children}) => <ul className="list-disc pl-4 mb-2 space-y-1">{children}</ul>,
                            ol: ({children}) => <ol className="list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
                            li: ({children}) => <li className="leading-relaxed">{children}</li>,
                            h1: ({children}) => <p className="font-bold text-slate-900 mb-1">{children}</p>,
                            h2: ({children}) => <p className="font-bold text-slate-900 mb-1">{children}</p>,
                            h3: ({children}) => <p className="font-semibold text-slate-800 mb-1">{children}</p>,
                            a: ({href, children}) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">{children}</a>,
                            code: ({children}) => <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">{children}</code>,
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    ) : msg.content}
                  </div>
                </div>
                )
              ))}
              {loading && (
                <div className="flex justify-start gap-2">
                  <OperatorAvatar src={operator.img} alt={operator.name} size="w-7 h-7" className="mt-1" />
                  <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-slate-100">
                    <p className="text-xs text-slate-400 mb-1.5">{operator.name} is typing</p>
                    <span className="inline-flex gap-1">
                      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input + Voice */}
            <div className="px-4 py-3 border-t border-slate-100 bg-white flex-shrink-0">
              {userMessageCount >= 2 && !messages.some(m => m.role === "tier") && (
                <button
                  data-testid="get-tier-recommendation-btn"
                  onClick={requestTierRecommendation}
                  disabled={recommending}
                  className="mb-2 w-full text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-100 rounded-full py-2 transition-colors disabled:opacity-60"
                >
                  {recommending ? "Working out the right tier..." : "Recommend the tier that fits"}
                </button>
              )}
              <AnimatePresence>
                {isRecording && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                    className="flex items-center gap-2 mb-2 px-1">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-xs text-red-500 font-medium">Recording {formatDuration(recordingDuration)}</span>
                    <span className="text-xs text-slate-400 ml-auto">Tap stop when done</span>
                  </motion.div>
                )}
              </AnimatePresence>
              {isTranscribing && (
                <div className="flex items-center gap-2 mb-2 px-1">
                  <div className="w-3 h-3 border border-blue-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs text-blue-500 font-medium">Transcribing...</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <input ref={inputRef} data-testid="assistant-chat-input" type="text" value={input}
                  onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                  placeholder={isRecording ? "Listening..." : `Message ${operator.name.split(" ")[0]}...`}
                  disabled={isRecording}
                  className="flex-1 bg-slate-50 border border-slate-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all placeholder:text-slate-400 disabled:opacity-50" />
                <button data-testid="assistant-mic-btn" onClick={toggleRecording} disabled={loading || isTranscribing}
                  className={`rounded-full p-2.5 transition-all ${
                    isRecording ? "bg-red-500 text-white hover:bg-red-600 animate-pulse" : "bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700"
                  } disabled:opacity-40 disabled:cursor-not-allowed`}>
                  {isRecording ? <Square size={14} /> : <Mic size={14} />}
                </button>
                <button data-testid="assistant-send-btn" onClick={sendMessage} disabled={!input.trim() || loading || isRecording}
                  className="bg-blue-600 text-white rounded-full p-2.5 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                  <Send size={14} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── FLOATING BUTTON ─── */}
      {stage === "closed" && (
        <div className="fixed bottom-3 right-3 md:bottom-6 md:right-4" style={{ zIndex: 2147483645 }}>
          <ChatToggleButton onClick={handleManualOpen} />
        </div>
      )}
    </>,
    document.body
  );
}
